/**
 * UsersPage Component
 *
 * Main page for managing users with search, filtering, and CRUD operations.
 * Includes invite user, view user details, edit user, delete user, and
 * cancel invitation functionality.
 */

import React, { useState, useMemo } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { DashboardLayout } from '../../../components/layout/DashboardLayout';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Alert } from '../../../components/ui/Alert';
import { UserTable, UserTableRow } from '../components/UserTable';
import { InviteUserModal } from '../components/InviteUserModal';
import { UserDetailsModal } from '../components/UserDetailsModal';
import { EditUserModal } from '../components/EditUserModal';
import { useUsers } from '../hooks/useUsers';
import { useInvitations } from '../hooks/useInvitations';
import { useDeleteUser } from '../hooks/useDeleteUser';
import { useCancelInvitation } from '../hooks/useCancelInvitation';
import { useAuth } from '../../../contexts/AuthContext';
import { hasPermission } from '../../../lib/utils/permissions';
import { useLayoutProps } from '../../../hooks/useLayoutProps';
import { useConfirm } from '../../../hooks/useConfirm';
import type { UserSanitized, InvitationSanitized } from '../../../types/user.types';
import { getErrorMessage } from '../../../lib/utils/errors';

export const UsersPage: React.FC = () => {
  const { user, tenant, activeRole } = useAuth();
  const layoutProps = useLayoutProps();
  const { confirm, ConfirmDialog } = useConfirm();

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [adminFilter, setAdminFilter] = useState<string>('all');
  const [showInviteModal, setShowInviteModal] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<UserSanitized | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  const tenantId = tenant?.tenantId || '';

  // Permission checks
  const canCreate = hasPermission(activeRole?.permissions, 'iam:user:create');
  const canUpdate = hasPermission(activeRole?.permissions, 'iam:user:update');
  const canDelete = hasPermission(activeRole?.permissions, 'iam:user:delete');

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch users and invitations
  const { data: users, isLoading: usersLoading, isFetching: usersFetching, error: usersError, refetch: refetchUsers } = useUsers(tenantId);
  const { data: invitations, isLoading: invitationsLoading, isFetching: invitationsFetching, error: invitationsError, refetch: refetchInvitations } = useInvitations(tenantId);
  const deleteUserMutation = useDeleteUser(tenantId);
  const cancelInvitationMutation = useCancelInvitation(tenantId);

  const isLoading = usersLoading || invitationsLoading;
  const isFetching = usersFetching || invitationsFetching;
  const error = usersError || invitationsError;

  const handleRefresh = () => {
    refetchUsers();
    refetchInvitations();
  };

  // Combine and filter users and invitations
  const tableRows = useMemo(() => {
    const rows: UserTableRow[] = [];

    // Add users
    if (users) {
      let filteredUsers = [...users];

      // Search filter for users
      if (debouncedSearch) {
        const query = debouncedSearch.toLowerCase();
        filteredUsers = filteredUsers.filter(
          (user) =>
            user.firstName.toLowerCase().includes(query) ||
            user.lastName.toLowerCase().includes(query) ||
            user.email.toLowerCase().includes(query)
        );
      }

      // Status filter (skip 'invited' for users)
      if (statusFilter !== 'all' && statusFilter !== 'invited') {
        filteredUsers = filteredUsers.filter((user) => user.status === statusFilter);
      }

      // Admin filter
      if (adminFilter === 'admin') {
        filteredUsers = filteredUsers.filter((user) => user.isAdmin);
      } else if (adminFilter === 'non-admin') {
        filteredUsers = filteredUsers.filter((user) => !user.isAdmin);
      }

      // Only add users if not filtering by 'invited' status
      if (statusFilter !== 'invited') {
        filteredUsers.forEach((user) => {
          rows.push({ type: 'user', data: user });
        });
      }
    }

    // Add invitations
    if (invitations && (statusFilter === 'all' || statusFilter === 'invited')) {
      let filteredInvitations = [...invitations];

      // Search filter for invitations
      if (debouncedSearch) {
        const query = debouncedSearch.toLowerCase();
        filteredInvitations = filteredInvitations.filter(
          (invitation) => invitation.email.toLowerCase().includes(query)
        );
      }

      // Admin filter doesn't apply to invitations (they don't have admin status yet)
      // Only show if adminFilter is 'all' (invitations could be either)
      if (adminFilter === 'all') {
        filteredInvitations.forEach((invitation) => {
          rows.push({ type: 'invitation', data: invitation });
        });
      }
    }

    // Sort by createdAt descending (newest first)
    rows.sort((a, b) => {
      const dateA = new Date(a.data.createdAt).getTime();
      const dateB = new Date(b.data.createdAt).getTime();
      return dateB - dateA;
    });

    return rows;
  }, [users, invitations, debouncedSearch, statusFilter, adminFilter]);

  // Calculate totals for stats
  const totalUsers = users?.length || 0;
  const totalInvitations = invitations?.length || 0;
  const totalItems = totalUsers + totalInvitations;

  // Event handlers
  const handleInviteSuccess = () => {
    refetchUsers();
    refetchInvitations();
  };

  const handleViewUser = (user: UserSanitized) => {
    setSelectedUser(user);
    setShowDetailsModal(true);
  };

  const handleEditUser = (user: UserSanitized) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleEditSuccess = () => {
    refetchUsers();
  };

  const handleDeleteUser = async (user: UserSanitized) => {
    const confirmed = await confirm({
      title: 'Delete User',
      message: `Are you sure you want to delete ${user.firstName} ${user.lastName}? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
    });

    if (confirmed) {
      try {
        await deleteUserMutation.mutateAsync(user.userId);
      } catch (error) {
        console.error('Failed to delete user:', error);
      }
    }
  };

  const handleCancelInvitation = async (invitation: InvitationSanitized) => {
    const confirmed = await confirm({
      title: 'Delete Invitation',
      message: `Are you sure you want to delete the invitation to ${invitation.email}? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
    });

    if (confirmed) {
      try {
        await cancelInvitationMutation.mutateAsync(invitation.invitationId);
      } catch (error) {
        console.error('Failed to cancel invitation:', error);
      }
    }
  };


  return (
    <DashboardLayout {...layoutProps}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Users</h1>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Manage your team members and their permissions
            </p>
          </div>
          {canCreate && (
            <Button
              variant="primary"
              onClick={() => setShowInviteModal(true)}
            >
              Invite User
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="search"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="sm:w-48">
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
              <option value="invited">Invited</option>
            </Select>
          </div>
          <div className="sm:w-48">
            <Select
              value={adminFilter}
              onChange={(e) => setAdminFilter(e.target.value)}
            >
              <option value="all">All Users</option>
              <option value="admin">Admins Only</option>
              <option value="non-admin">Non-Admins Only</option>
            </Select>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleRefresh}
            disabled={isFetching}
          >
            <ArrowPathIcon className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="error">
            Failed to load users: {getErrorMessage(error)}
          </Alert>
        )}

        {deleteUserMutation.isError && (
          <Alert variant="error">
            Failed to delete user: {getErrorMessage(deleteUserMutation.error)}
          </Alert>
        )}

        {cancelInvitationMutation.isError && (
          <Alert variant="error">
            Failed to cancel invitation: {getErrorMessage(cancelInvitationMutation.error)}
          </Alert>
        )}

        {/* User Table */}
        <div className="card shadow-sm rounded-lg overflow-hidden">
          <UserTable
            rows={tableRows}
            isLoading={isLoading}
            onViewUser={handleViewUser}
            onEditUser={handleEditUser}
            onDeleteUser={handleDeleteUser}
            onCancelInvitation={handleCancelInvitation}
            canEdit={canUpdate}
            canDelete={canDelete}
          />
        </div>

        {/* Stats */}
        {!isLoading && (
          <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Showing {tableRows.length} of {totalItems} ({totalUsers} users, {totalInvitations} pending invitations)
          </div>
        )}

        {/* Modals */}
        <InviteUserModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          onSuccess={handleInviteSuccess}
        />

        <UserDetailsModal
          user={selectedUser}
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedUser(null);
          }}
        />

        <EditUserModal
          user={selectedUser}
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
          onSuccess={handleEditSuccess}
        />

        {/* Confirmation Dialog */}
        {ConfirmDialog}
      </div>
    </DashboardLayout>
  );
};

export default UsersPage;
